import { useEffect, useMemo, useState } from 'react';
import { collection, doc, getDocs, serverTimestamp, updateDoc } from 'firebase/firestore';
import { Edit, RefreshCw, Search } from 'lucide-react';
import { ERPUser } from '../../core/auth/types';
import { db } from '../../core/firebase/firebase';

type AccountingRow = {
  id: string;
  bookingCode: string;
  clientName: string;
  supplierName: string;
  serviceType: string;
  subServiceType: string;
  department: string;
  sellAmount: number;
  netAmount: number;
  receivedAmount: number;
  supplierPaidAmount: number;
  refundAmount: number;
  profit: number;
  outstandingAmount: number;
  supplierBalance: number;
  paymentMode: string;
  supplierPaymentMode: string;
  paymentReference: string;
  supplierPaymentReference: string;
  paymentStatus: string;
  supplierPaymentStatus: string;
  bookingStatus: string;
  invoiceStatus: string;
  receiptStatus: string;
  refundStatus: string;
  invoiceNo: string;
  receiptNo: string;
  refundNo: string;
  accountingStatus: string;
  paymentNotes: string;
};

function money(value: unknown) {
  const numericValue = Number(value === '' || value === null || value === undefined ? 0 : value);
  return Number.isFinite(numericValue) ? numericValue : 0;
}

function paymentStatusFor(total: number, paid: number, refund = 0) {
  if (refund > 0) return 'refunded';
  if (total > 0 && paid >= total) return 'paid';
  if (paid > 0) return 'partial';
  return 'unpaid';
}

function makeCode(prefix: string) {
  return `${prefix}-${Math.floor(100000 + Math.random() * 900000)}`;
}

function normalizeAccountingRow(id: string, raw: Record<string, any>): AccountingRow {
  const sellAmount = money(raw.sellAmount);
  const netAmount = money(raw.netAmount);
  const receivedAmount = money(raw.receivedAmount);
  const supplierPaidAmount = money(raw.supplierPaidAmount);
  const refundAmount = money(raw.refundAmount);
  const profit = sellAmount - netAmount - refundAmount;
  const outstandingAmount = Math.max(sellAmount - receivedAmount - refundAmount, 0);
  const supplierBalance = Math.max(netAmount - supplierPaidAmount, 0);

  return {
    id,
    bookingCode: String(raw.bookingCode || id),
    clientName: String(raw.clientName || ''),
    supplierName: String(raw.supplierName || ''),
    serviceType: String(raw.serviceType || ''),
    subServiceType: String(raw.subServiceType || ''),
    department: String(raw.department || ''),
    sellAmount,
    netAmount,
    receivedAmount,
    supplierPaidAmount,
    refundAmount,
    profit,
    outstandingAmount,
    supplierBalance,
    paymentMode: String(raw.paymentMode || ''),
    supplierPaymentMode: String(raw.supplierPaymentMode || ''),
    paymentReference: String(raw.paymentReference || ''),
    supplierPaymentReference: String(raw.supplierPaymentReference || ''),
    paymentStatus: String(raw.paymentStatus || paymentStatusFor(sellAmount, receivedAmount, refundAmount)),
    supplierPaymentStatus: String(raw.supplierPaymentStatus || paymentStatusFor(netAmount, supplierPaidAmount)),
    bookingStatus: String(raw.bookingStatus || ''),
    invoiceStatus: String(raw.invoiceStatus || 'pending'),
    receiptStatus: String(raw.receiptStatus || 'pending'),
    refundStatus: String(raw.refundStatus || 'none'),
    invoiceNo: String(raw.invoiceNo || ''),
    receiptNo: String(raw.receiptNo || ''),
    refundNo: String(raw.refundNo || ''),
    accountingStatus: String(raw.accountingStatus || 'open'),
    paymentNotes: String(raw.paymentNotes || '')
  };
}

async function loadBookingAccounting(): Promise<AccountingRow[]> {
  const snapshot = await getDocs(collection(db, 'bookings'));
  return snapshot.docs.map((bookingDoc) => normalizeAccountingRow(bookingDoc.id, bookingDoc.data() as Record<string, any>));
}

function canEditAccounting(user: ERPUser) {
  return user.role === 'super_admin' || user.role === 'admin' || user.department === 'account' || Boolean((user as any).canViewFinance);
}

export default function AccountingPage({ user }: { user: ERPUser }) {
  const [rows, setRows] = useState<AccountingRow[]>([]);
  const [search, setSearch] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<AccountingRow | null>(null);
  const [form, setForm] = useState<Partial<AccountingRow>>({});
  const [busy, setBusy] = useState(false);

  async function load() {
    setLoading(true);
    setMessage('');
    try {
      setRows(await loadBookingAccounting());
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Unable to load accounting.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const queryText = search.toLowerCase();
    return rows.filter((row) => {
      if (!queryText) return true;
      return [
        row.bookingCode,
        row.clientName,
        row.supplierName,
        row.serviceType,
        row.subServiceType,
        row.paymentMode,
        row.paymentStatus,
        row.supplierPaymentStatus,
        row.bookingStatus,
        row.invoiceNo,
        row.receiptNo
      ].join(' ').toLowerCase().includes(queryText);
    });
  }, [rows, search]);

  const totals = useMemo(() => ({
    sell: rows.reduce((sum, item) => sum + item.sellAmount, 0),
    received: rows.reduce((sum, item) => sum + item.receivedAmount, 0),
    outstanding: rows.reduce((sum, item) => sum + item.outstandingAmount, 0),
    supplierPaid: rows.reduce((sum, item) => sum + item.supplierPaidAmount, 0),
    supplierBalance: rows.reduce((sum, item) => sum + item.supplierBalance, 0),
    refund: rows.reduce((sum, item) => sum + item.refundAmount, 0),
    profit: rows.reduce((sum, item) => sum + item.profit, 0)
  }), [rows]);

  function beginEdit(row: AccountingRow) {
    if (!canEditAccounting(user)) {
      setMessage('You do not have permission to edit accounting.');
      return;
    }
    setEditing(row);
    setForm(row);
    setMessage('');
  }

  async function saveAccounting() {
    if (!editing) return;

    const sellAmount = money(form.sellAmount);
    const netAmount = money(form.netAmount);
    const receivedAmount = money(form.receivedAmount);
    const supplierPaidAmount = money(form.supplierPaidAmount);
    const refundAmount = money(form.refundAmount);
    const profit = sellAmount - netAmount - refundAmount;
    const outstandingAmount = Math.max(sellAmount - receivedAmount - refundAmount, 0);
    const supplierBalance = Math.max(netAmount - supplierPaidAmount, 0);
    const paymentStatus = paymentStatusFor(sellAmount, receivedAmount, refundAmount);
    const supplierPaymentStatus = paymentStatusFor(netAmount, supplierPaidAmount, 0);

    const payload = {
      sellAmount,
      netAmount,
      receivedAmount,
      supplierPaidAmount,
      refundAmount,
      profit,
      outstandingAmount,
      supplierBalance,
      paymentStatus,
      supplierPaymentStatus,
      paymentMode: form.paymentMode || 'zelle',
      supplierPaymentMode: form.supplierPaymentMode || 'bank_transfer',
      paymentReference: form.paymentReference || '',
      supplierPaymentReference: form.supplierPaymentReference || '',
      invoiceStatus: form.invoiceStatus || 'pending',
      receiptStatus: form.receiptStatus || (receivedAmount > 0 ? 'issued' : 'pending'),
      refundStatus: refundAmount > 0 ? 'processed' : (form.refundStatus || 'none'),
      invoiceNo: form.invoiceNo || (sellAmount > 0 ? makeCode('INV') : ''),
      receiptNo: form.receiptNo || (receivedAmount > 0 ? makeCode('RCT') : ''),
      refundNo: form.refundNo || (refundAmount > 0 ? makeCode('RF') : ''),
      accountingStatus: outstandingAmount === 0 && supplierBalance === 0 ? 'closed' : 'open',
      paymentNotes: form.paymentNotes || '',
      updatedAt: serverTimestamp(),
      updatedBy: user.uid
    };

    setBusy(true);
    try {
      await updateDoc(doc(db, 'bookings', editing.id), payload);
      setEditing(null);
      setForm({});
      await load();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Unable to save accounting.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <p className="eyebrow">Accounting Operations</p>
          <h1>Accounting Pro</h1>
          <p>Booking-linked receivable, receipt, refund, supplier payable, payment mode, profit and outstanding control.</p>
        </div>
        <button className="btn" title="Refresh accounting" onClick={load}><RefreshCw size={18}/> Refresh</button>
      </div>

      {message && <div className="error-box" style={{ marginBottom: 20 }}>{message}</div>}

      <div className="reminder-top-grid">
        <section className="card reminder-card"><h3>Total Sales</h3><p className="muted"><b>${totals.sell.toFixed(2)}</b></p></section>
        <section className="card reminder-card"><h3>Received</h3><p className="muted"><b>${totals.received.toFixed(2)}</b></p></section>
        <section className="card reminder-card"><h3>Outstanding</h3><p className="muted"><b>${totals.outstanding.toFixed(2)}</b></p></section>
        <section className="card reminder-card"><h3>Supplier Paid</h3><p className="muted"><b>${totals.supplierPaid.toFixed(2)}</b></p></section>
        <section className="card reminder-card"><h3>Supplier Payable</h3><p className="muted"><b>${totals.supplierBalance.toFixed(2)}</b></p></section>
        <section className="card reminder-card"><h3>Refund</h3><p className="muted"><b>${totals.refund.toFixed(2)}</b></p></section>
        <section className="card reminder-card"><h3>Profit</h3><p className="muted"><b>${totals.profit.toFixed(2)}</b></p></section>
      </div>

      <div className="toolbar" style={{ marginTop: 20 }}>
        <div style={{ position: 'relative' }}>
          <Search size={18} style={{ position: 'absolute', left: 16, top: 18, color: '#66758a' }} />
          <input style={{ paddingLeft: 44 }} placeholder="Search booking, client, supplier, invoice, receipt, payment mode" value={search} onChange={(event) => setSearch(event.target.value)} />
        </div>
      </div>

      <section className="card table-card" style={{ marginTop: 20 }}>
        <table>
          <thead>
            <tr>
              <th>Booking</th>
              <th>Client</th>
              <th>Service</th>
              <th>Sell / Received</th>
              <th>Outstanding</th>
              <th>Net / Supplier Paid</th>
              <th>Supplier Balance</th>
              <th>Profit</th>
              <th>Invoice / Receipt</th>
              <th>Payment Mode</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={12} className="empty">Loading accounting...</td></tr>}
            {!loading && filtered.length === 0 && <tr><td colSpan={12} className="empty">No accounting records found. Add bookings first.</td></tr>}
            {!loading && filtered.map((row) => (
              <tr key={row.id}>
                <td><strong>{row.bookingCode}</strong><br/><small>{row.supplierName || '-'}</small></td>
                <td>{row.clientName || '-'}</td>
                <td>{row.serviceType}<br/><small>{row.subServiceType}</small></td>
                <td>${row.sellAmount.toFixed(2)}<br/><small>Rec: ${row.receivedAmount.toFixed(2)}</small></td>
                <td>${row.outstandingAmount.toFixed(2)}</td>
                <td>${row.netAmount.toFixed(2)}<br/><small>Paid: ${row.supplierPaidAmount.toFixed(2)}</small></td>
                <td>${row.supplierBalance.toFixed(2)}</td>
                <td>${row.profit.toFixed(2)}</td>
                <td><small>INV: {row.invoiceNo || '-'}</small><br/><small>RCT: {row.receiptNo || '-'}</small></td>
                <td>{row.paymentMode || '-'}<br/><small>{row.paymentReference || ''}</small></td>
                <td><span className="pill">{row.paymentStatus}</span><br/><small>{row.accountingStatus}</small></td>
                <td className="actions"><button title="Edit accounting" onClick={() => beginEdit(row)}><Edit size={16}/></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {editing && (
        <div className="modal-backdrop">
          <div className="modal large">
            <div className="modal-head">
              <h2>Edit Accounting — {editing.bookingCode}</h2>
              <button className="icon-only" title="Close" onClick={() => setEditing(null)}>×</button>
            </div>

            <div className="form-section-title">Client Receivable</div>
            <div className="form-grid">
              <label>Sell Amount<input type="number" value={form.sellAmount === 0 ? '' : (form.sellAmount ?? '')} onChange={(event) => setForm({ ...form, sellAmount: event.target.value === '' ? 0 : Number(event.target.value) })}/></label>
              <label>Received Amount<input type="number" value={form.receivedAmount === 0 ? '' : (form.receivedAmount ?? '')} onChange={(event) => setForm({ ...form, receivedAmount: event.target.value === '' ? 0 : Number(event.target.value) })}/></label>
              <label>Payment Mode<select value={form.paymentMode || 'zelle'} onChange={(event) => setForm({ ...form, paymentMode: event.target.value })}><option>cash</option><option>zelle</option><option>venmo</option><option>check</option><option>credit_card</option><option>bank_transfer</option><option>other</option></select></label>
              <label>Payment Reference<input value={form.paymentReference || ''} onChange={(event) => setForm({ ...form, paymentReference: event.target.value })}/></label>
              <label>Invoice Status<select value={form.invoiceStatus || 'pending'} onChange={(event) => setForm({ ...form, invoiceStatus: event.target.value })}><option>pending</option><option>issued</option><option>not_required</option></select></label>
              <label>Invoice No<input value={form.invoiceNo || ''} onChange={(event) => setForm({ ...form, invoiceNo: event.target.value })}/></label>
              <label>Receipt Status<select value={form.receiptStatus || 'pending'} onChange={(event) => setForm({ ...form, receiptStatus: event.target.value })}><option>pending</option><option>issued</option><option>not_required</option></select></label>
              <label>Receipt No<input value={form.receiptNo || ''} onChange={(event) => setForm({ ...form, receiptNo: event.target.value })}/></label>
              <label>Refund Amount<input type="number" value={form.refundAmount === 0 ? '' : (form.refundAmount ?? '')} onChange={(event) => setForm({ ...form, refundAmount: event.target.value === '' ? 0 : Number(event.target.value) })}/></label>
              <label>Refund No<input value={form.refundNo || ''} onChange={(event) => setForm({ ...form, refundNo: event.target.value })}/></label>
            </div>

            <div className="form-section-title">Supplier Payable</div>
            <div className="form-grid">
              <label>Net Amount<input type="number" value={form.netAmount === 0 ? '' : (form.netAmount ?? '')} onChange={(event) => setForm({ ...form, netAmount: event.target.value === '' ? 0 : Number(event.target.value) })}/></label>
              <label>Supplier Paid<input type="number" value={form.supplierPaidAmount === 0 ? '' : (form.supplierPaidAmount ?? '')} onChange={(event) => setForm({ ...form, supplierPaidAmount: event.target.value === '' ? 0 : Number(event.target.value) })}/></label>
              <label>Supplier Payment Mode<select value={form.supplierPaymentMode || 'bank_transfer'} onChange={(event) => setForm({ ...form, supplierPaymentMode: event.target.value })}><option>cash</option><option>zelle</option><option>venmo</option><option>check</option><option>credit_card</option><option>bank_transfer</option><option>other</option></select></label>
              <label>Supplier Payment Ref<input value={form.supplierPaymentReference || ''} onChange={(event) => setForm({ ...form, supplierPaymentReference: event.target.value })}/></label>
              <label className="span-3">Payment Notes<textarea value={form.paymentNotes || ''} onChange={(event) => setForm({ ...form, paymentNotes: event.target.value })}/></label>
            </div>

            <div className="modal-foot">
              <button className="btn" onClick={() => setEditing(null)}>Cancel</button>
              <button className="btn primary" disabled={busy} onClick={saveAccounting}>{busy ? 'Saving...' : 'Save Accounting'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
